// Client-side print pipeline: loads the article embed into a hidden
// same-origin iframe, applies the print header/CSS/cleanups to its document,
// and opens the browser print dialog scoped to that iframe.
import robotoFontUrl from '../../styles/fonts/Roboto.woff2?url';
import { waitFor } from '../wait-for';
import { injectPdfHeader } from './inject-header';
import { sanitizePdfStyles } from './sanitize-styles';
import { wrapHeadingsWithNextSibling } from './wrap-headings';
import {
  ExportContentToPdfOptions,
  PdfExportError,
  PdfHeaderData,
} from './types';

const LOAD_TIMEOUT_MS = 30_000;
const ASSET_TIMEOUT_MS = 10_000;
const POLL_TIMEOUT_MS = 8_000;
// Safari sometimes never fires afterprint on iframe windows — a stale hidden
// iframe is harmless, so a generous fallback timer is enough.
const CLEANUP_FALLBACK_MS = 120_000;
const FRAME_ATTR = 'data-pdf-export-frame';

let inFlight = false;

// Applies all V1 transformations to the embed document, in the V1 order:
// header + print CSS first (CSS must exist before layout-measuring passes),
// then style sanitation, then heading-orphan wrapping.
export function prepareDocumentForPrint(
  doc: Document,
  data: PdfHeaderData,
  documentTitle: string
): void {
  injectPdfHeader(doc, data);
  sanitizePdfStyles(doc);
  wrapHeadingsWithNextSibling(doc);
  // Chrome suggests the document title as the Save-as-PDF filename.
  doc.title = documentTitle;
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const removeStaleFrames = (): void => {
  document.querySelectorAll(`iframe[${FRAME_ATTR}]`).forEach((f) => f.remove());
};

// Rendered-but-invisible: display:none breaks both printing (Firefox refuses)
// and the cleanup passes, which measure layout (offsetHeight,
// getBoundingClientRect). 794×1123 = A4 at 96dpi, matching the V1 Playwright
// viewport so JS-measured heights predict print layout.
const createHiddenFrame = (): HTMLIFrameElement => {
  const iframe = document.createElement('iframe');
  iframe.setAttribute(FRAME_ATTR, '');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.tabIndex = -1;
  iframe.style.cssText =
    'position: fixed; right: 0; bottom: 0; width: 794px; height: 1123px;' +
    'visibility: hidden; pointer-events: none; border: 0; z-index: -1;';
  return iframe;
};

const loadFrame = (iframe: HTMLIFrameElement, url: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(
        new PdfExportError('LOAD_TIMEOUT', 'Timed out loading article embed')
      );
    }, LOAD_TIMEOUT_MS);
    iframe.addEventListener(
      'load',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
    iframe.src = url;
  });

const waitForAssets = async (doc: Document): Promise<void> => {
  const imagePromises = Array.from(doc.images).map((img) => {
    if (img.complete || typeof img.decode !== 'function') {
      return Promise.resolve();
    }
    // decode() waits for the image to load, then decodes; a rejection
    // (broken image) must not block printing.
    return img.decode().catch(() => undefined);
  });
  const fontsReady: Promise<unknown> = doc.fonts?.ready ?? Promise.resolve();
  await Promise.race([
    Promise.allSettled([fontsReady, ...imagePromises]),
    delay(ASSET_TIMEOUT_MS),
  ]);
};

// Poll blocks hydrate asynchronously (donkey polls.js fetches the poll state and
// then adds `-js-ready` to each poll's `.-js-options`, replacing the spinner).
// We print after they settle so the PDF shows the poll as the user sees it
// on-screen. If a poll can't hydrate in time, we don't block — the print CSS
// (buildPrintStyles) hides the un-`-js-ready` spinner so "Loading …" never
// prints.
// Exported for unit testing; `timeoutMs` defaults to POLL_TIMEOUT_MS in prod.
export const waitForPolls = async (
  doc: Document,
  timeoutMs: number = POLL_TIMEOUT_MS
): Promise<void> => {
  if (!doc.querySelector('.-js-poll')) return; // no polls in this article
  await waitFor(
    () => doc.querySelectorAll('.-js-options:not(.-js-ready)').length === 0,
    timeoutMs
  ).catch(() => undefined); // timeout → CSS fallback hides the leftover spinner
};

// Cleanup: remove the iframe and restore the parent document title (which we
// override below so Chrome's Save-as-PDF uses our filename). Runs on the first
// of afterprint or the fallback timer; idempotent and clears the pending timer
// so it never restores a (possibly since-changed) title a second time.
const scheduleCleanup = (
  iframe: HTMLIFrameElement,
  win: Window,
  restoreTitle: () => void
): void => {
  let done = false;
  let timer: ReturnType<typeof setTimeout>;
  const cleanup = (): void => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    win.removeEventListener('afterprint', cleanup);
    iframe.remove();
    restoreTitle();
  };
  win.addEventListener('afterprint', cleanup, { once: true });
  timer = setTimeout(cleanup, CLEANUP_FALLBACK_MS);
};

export async function exportContentToPdf({
  embedUrl,
  headerData,
  documentTitle,
}: ExportContentToPdfOptions): Promise<void> {
  if (inFlight) return;
  inFlight = true;
  removeStaleFrames();
  const iframe = createHiddenFrame();
  document.body.appendChild(iframe);
  try {
    await loadFrame(iframe, embedUrl);
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !win) {
      throw new PdfExportError('EMBED_ERROR', 'Embed document inaccessible');
    }
    // The embed controller renders error.ejs (<title>Error</title>, <h1>Error</h1>)
    // when the upstream article fetch fails.
    if (
      doc.title === 'Error' &&
      doc.querySelector('h1')?.textContent === 'Error'
    ) {
      throw new PdfExportError('EMBED_ERROR', 'Article embed failed to load');
    }
    prepareDocumentForPrint(
      doc,
      { ...headerData, robotoFontUrl },
      documentTitle
    );
    await waitForAssets(doc);
    await waitForPolls(doc);
    // Chrome derives the Save-as-PDF filename from the TOP-LEVEL document title
    // when printing an iframe — setting the iframe's own title is not enough.
    // Override the parent title for the print, restore it on cleanup.
    const previousTitle = document.title;
    document.title = documentTitle;
    scheduleCleanup(iframe, win, () => {
      document.title = previousTitle;
    });
    win.focus();
    win.print();
  } catch (err) {
    iframe.remove();
    if (err instanceof PdfExportError) throw err;
    throw new PdfExportError(
      'PRINT_FAILED',
      err instanceof Error ? err.message : 'PDF export failed'
    );
  } finally {
    inFlight = false;
  }
}
